import type {ReactElement} from 'react';
import {useState} from 'react';
import React from 'react';
import styles from './ai-prompt-box-attachment-preview.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize} from '@Components/icon-v2/icon.types';
import type {AiPromptBoxAttachmentPreviewProps} from '@Components/ai-prompt-box/components/ai-prompt-box-attachment-preview/ai-prompt-box-attachment-preview.types';
import {DOCUMENT_CHIP_STEP_BY_STATUS} from '@Components/ai-prompt-box/components/ai-prompt-box-attachment-preview/ai-prompt-box-attachment-preview.types';
import {AiPromptBoxSize} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {isVideoUrl} from '@Utils/media.util';
import {PendingUploadStatus} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {DocumentChip} from '@Components/document-chip/document-chip';
import {AttachmentRemoveButton} from '@Components/ai-prompt-box/components/attachment-remove-button/attachment-remove-button';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';

const SIZE_DIMENSION_MAP = {
  [AiPromptBoxSize.SMALL]: 48,
  [AiPromptBoxSize.MEDIUM]: 56,
  [AiPromptBoxSize.SINGLE_LINE]: 48,
  [AiPromptBoxSize.SMALL_EXTENDED]: 48,
};

const SIZE_CLASS_MAP = {
  [AiPromptBoxSize.SMALL]: styles.small,
  [AiPromptBoxSize.MEDIUM]: styles.medium,
  [AiPromptBoxSize.SINGLE_LINE]: styles.small,
  [AiPromptBoxSize.SMALL_EXTENDED]: styles.small,
};

export function AiPromptBoxAttachmentPreview({previewUrl, index, onRemove, onPreviewClick, onRetry, size, pendingUpload, isRemovalLocked, documentAttachment}: AiPromptBoxAttachmentPreviewProps): ReactElement {
  const [isMediaLoaded, setMediaLoaded] = useState(false);
  const isDocument = documentAttachment !== undefined;
  const loaded = isMediaLoaded || isDocument;

  const dimension = SIZE_DIMENSION_MAP[size];
  const isVideo = isVideoUrl(previewUrl);
  const isInProgress = pendingUpload !== undefined && pendingUpload.status !== PendingUploadStatus.FAILED;
  const isFailed = pendingUpload?.status === PendingUploadStatus.FAILED;
  const showOverlay = !isDocument && (isFailed || isInProgress || !loaded);
  const showOverlayDim = isFailed || isInProgress;
  const canRetryDocument = isDocument && isFailed && pendingUpload?.failure?.isRetryable === true;
  const failedClass = isFailed ? styles.failed : '';

  const handleMediaLoad = (): void => {
    setMediaLoaded(true);
  };

  const handleMediaClick = (): void => {
    onPreviewClick(index);
  };

  const handleRemoveClick = (): void => {
    onRemove(index);
  };

  const handleRetryClick = (): void => {
    onRetry?.(index);
  };

  const renderMedia = (): ReactElement => {
    const commonClassName = `${styles.attachmentItem} ${loaded ? '' : '_hidden'} ${failedClass}`;

    if (documentAttachment) {
      return (
        <DocumentChip
          filename={documentAttachment.filename}
          documentType={documentAttachment.documentType}
          onClick={handleMediaClick}
          isDisabled={pendingUpload !== undefined}
          step={pendingUpload === undefined ? undefined : DOCUMENT_CHIP_STEP_BY_STATUS[pendingUpload.status]}
          failure={pendingUpload?.failure}
        />
      );
    }

    if (isVideo) {
      return <video src={previewUrl} className={commonClassName} onLoadedData={handleMediaLoad} onClick={handleMediaClick} muted />;
    }

    return <img src={previewUrl} className={commonClassName} onLoad={handleMediaLoad} onClick={handleMediaClick} alt="" />;
  };

  const renderOverlayContent = (): ReactElement => {
    if (isFailed && onRetry) {
      return (
        <Icon
          className={styles.retryButton}
          icon={'icon-rotate-cw'}
          size={IconSize.SIZE_ICON_20}
          shape={IconShape.NONE}
          ariaLabel={window.i18next.t('pmwjs_retry_upload')}
          onClick={handleRetryClick}
        />
      );
    }
    return <CircularProgressLoader color={LOADER_COLOR.DARK} size={LOADER_SIZE.XXSMALL} classes="_unmargin" />;
  };

  const renderOverlay = (): ReactElement | null => {
    if (!showOverlay) {
      return null;
    }
    return (
      <div className={`${styles.uploadingOverlay} ${showOverlayDim ? styles.uploadingOverlayDim : ''} ${failedClass}`}>
        {renderOverlayContent()}
      </div>
    );
  };

  const renderDocumentRetryButton = (): ReactElement | null => {
    if (!canRetryDocument) {
      return null;
    }
    return (
      <button type="button" className={styles.documentRetryButton} onClick={handleRetryClick} aria-label={window.i18next.t('pmwjs_retry_upload')} title={window.i18next.t('pmwjs_retry_upload')}>
        <Icon icon="icon-rotate-cw" size={IconSize.SIZE_ICON_12} ariaHidden />
      </button>
    );
  };

  const renderRemoveButton = (): ReactElement | null => {
    if (isRemovalLocked) {
      return null;
    }
    return <AttachmentRemoveButton onClick={handleRemoveClick} />;
  };

  return (
    <div className={`${styles.attachment} ${SIZE_CLASS_MAP[size]}`} aria-busy={isInProgress}>
      <div
        className={`${styles.attachmentItem} ${styles.placeholder} ${failedClass} ${loaded ? '_hidden' : ''} spacing-m-b-2`}
        style={{width: dimension, height: dimension}}
      />
      {renderMedia()}
      {renderOverlay()}
      {renderDocumentRetryButton()}
      {renderRemoveButton()}
    </div>
  );
}

export default React.memo(AiPromptBoxAttachmentPreview, (prevProps, nextProps) => {
  return (
    prevProps.previewUrl === nextProps.previewUrl &&
    prevProps.size === nextProps.size &&
    prevProps.index === nextProps.index &&
    prevProps.pendingUpload?.status === nextProps.pendingUpload?.status &&
    prevProps.pendingUpload?.failure === nextProps.pendingUpload?.failure &&
    prevProps.isRemovalLocked === nextProps.isRemovalLocked &&
    prevProps.documentAttachment?.filename === nextProps.documentAttachment?.filename
  );
});
